import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, Button, Row, Col } from "reactstrap";
import AddonSelector from "./AddonSelector";
import CompletionScreen from "./CompletionScreen";

const POSAddonModal = ({
  modal,
  toggleModal,
  product,
  addonQuantities,
  setAddonQuantities,
  handleAddToCartWithAddons,
}) => {
  const [subtotal, setSubtotal] = useState(parseFloat(product.price) || 0);
  const [currentAddonIndex, setCurrentAddonIndex] = useState(0);
  const [addonOrder, setAddonOrder] = useState(product.addons || []);
  const currentAddon = addonOrder[currentAddonIndex];

  useEffect(() => {
    setAddonOrder(product.addons || []);
  }, [product.addons]);

  const handleChange = (addon, option) => {
    const addonId = addon.id;
    const optionId = option.id;

    setAddonQuantities((prev) => {
      const updated = { ...prev };
      if (addon.is_multiple) {
        updated[addonId] = {
          ...(updated[addonId] || {}),
          [optionId]: 1,
        };
      } else {
        updated[addonId] = { [optionId]: 1 };
      }
      return updated;
    });
  };

  useEffect(() => {
    let total = parseFloat(product.price) || 0;
    addonOrder.forEach((addon) => {
      const selectedOptions = addonQuantities[addon.id] || {};
      Object.keys(selectedOptions).forEach((optionId) => {
        const option = addon.options.find((o) => o.id === optionId);
        if (option && selectedOptions[optionId]) {
          total += parseFloat(option.price || 0);
        }
      });
    });
    setSubtotal(total);
  }, [addonQuantities, product, addonOrder]);

  const validateRequiredFields = useCallback(() => {
    for (const addon of addonOrder) {
      if (addon.is_required) {
        const selectedOptions = addonQuantities[addon.id] || {};
        const hasSelection = Object.keys(selectedOptions).some(
          (optionId) => selectedOptions[optionId]
        );
        if (!hasSelection) {
          toast.error(`Please select a required option for "${addon.title}".`);
          return false;
        }
      }
    }
    return true;
  }, [addonOrder, addonQuantities]);

  const prepareAddonData = useCallback(() => {
    const formattedAddons = {};
    addonOrder.forEach((addon) => {
      const selectedOptions = addonQuantities[addon.id] || {};
      const selected = Object.keys(selectedOptions)
        .filter((optionId) => selectedOptions[optionId])
        .map((optionId) => {
          const option = addon.options.find((o) => o.id === optionId);
          return option
            ? `${option.option}${
                option.price ? ` (+$${parseFloat(option.price).toFixed(2)})` : ""
              }`
            : null;
        })
        .filter(Boolean)
        .join(", ");
      if (selected) {
        formattedAddons[addon.title] = selected;
      }
    });
    return JSON.stringify(formattedAddons);
  }, [addonOrder, addonQuantities]);

  const getAllSelectedOptions = useCallback(() => {
    const allSelected = [];
    addonOrder.forEach((addon) => {
      const selectedOptions = addonQuantities[addon.id] || {};
      Object.keys(selectedOptions)
        .filter((optionId) => selectedOptions[optionId])
        .forEach((optionId) => {
          const option = addon.options.find((o) => o.id === optionId);
          if (option) {
            allSelected.push({
              addonTitle: addon.title,
              option: option.option,
              price: parseFloat(option.price || 0),
            });
          }
        });
    });
    return allSelected;
  }, [addonOrder, addonQuantities]);

  const allSelectedOptions = getAllSelectedOptions();

  const handleAddToCart = useCallback(() => {
    if (!validateRequiredFields()) return;
    const selectedAddons = prepareAddonData();
    handleAddToCartWithAddons(selectedAddons, subtotal);
  }, [validateRequiredFields, prepareAddonData, handleAddToCartWithAddons, subtotal]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!currentAddon && e.key === "Enter" && modal && subtotal > 0) {
        e.preventDefault();
        handleAddToCart();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modal, subtotal, handleAddToCart, currentAddon]);

  return (
    <Modal
      isOpen={modal}
      toggle={toggleModal}
      size="lg"
      centered
      style={{ border: "none" }}
      contentClassName="bg-dark text-white"
    >
      <ModalBody style={{ backgroundColor: "#000", color: "#fff" }}>
        <Row>
          <Col md="12">
            {!currentAddon ? (
              <CompletionScreen subtotal={subtotal} allSelectedOptions={allSelectedOptions} />
            ) : (
              <AddonSelector
                addon={currentAddon}
                onSelect={handleChange}
                onNext={() => setCurrentAddonIndex(prev => prev + 1)}
                onBack={() => setCurrentAddonIndex(prev => prev - 1)}
                showBack={currentAddonIndex > 0}
                selectedOptions={addonQuantities}
              />
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter style={{ backgroundColor: "#000", color: "#fff", border: "none", flexDirection: "column", alignItems: "stretch" }}>
        {!currentAddon && (
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0" style={{ color: "#fff" }}>Total: ${subtotal.toFixed(2)}</h5>
            <div>
              <Button color="success" onClick={handleAddToCart} disabled={subtotal <= 0} className="me-2">
                Add to Cart (Enter)
              </Button>
              <Button color="danger" onClick={toggleModal}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default POSAddonModal;
